// Jobs widget: data-365="jobs"  (Venue Jobs, BACKLOG 0ag)
// Lists the venue's open positions → detail → apply form, entirely on the venue's own site (no 365 login:
// applicants are the public). Talks to the anonymous embed endpoints keyed by the venue's publishable key:
//   GET  /api/embed/{key}/jobs               open postings
//   POST /api/embed/{key}/jobs/{id}/apply    multipart/form-data (résumé optional)
// Spam protection is cheap and server-enforced: a hidden honeypot field ("website") and the time the form was
// on screen (elapsedMs, plus renderedAt as a fallback) — the server fakes success for bots and stores nothing.
// A per-IP rate limit (5/hour) comes back as HTTP 429, shown here as a friendly message.
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, clear, footer, renderLoading } from '../ui';
import { handleFetchFailure } from '../maintenance';
import { track, trackIds } from '../analytics';

interface JobQuestion { prompt: string; required: boolean; }
interface JobPosting {
  id: string;
  title: string;
  employmentType: string;
  employmentTypeLabel: string;
  description: string;
  payText?: string | null;
  startDate?: string | null;
  closeDate?: string | null;
  minimumAge: number;
  questions: JobQuestion[];
}

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXT = ['.pdf', '.docx', '.doc', '.txt', '.rtf'];
const TITLE = 'Join our team';

// Form controls the base stylesheet doesn't cover (textarea / tel / file / checkbox) + job cards.
const JOBS_CSS = `
.e365 textarea, .e365 input[type=tel], .e365 input[type=file] {
  width: 100%; padding: 9px 10px; font: inherit; color: inherit;
  border: 1px solid var(--e365-border); border-radius: 8px; background: var(--e365-surface);
}
.e365 textarea { min-height: 84px; resize: vertical; }
.e365 .e365-job { border: 1px solid var(--e365-border); border-radius: var(--e365-radius); padding: 14px; background: var(--e365-surface); margin-bottom: 10px; }
.e365 .e365-job h4 { margin: 0 0 4px; font-size: 1.05rem; }
.e365 .e365-job-meta { color: var(--e365-muted); font-size: .85rem; margin-bottom: 6px; }
.e365 .e365-job-desc { white-space: pre-wrap; font-size: .92rem; }
.e365 .e365-job .e365-btn { width: auto; margin-top: 10px; padding: 9px 16px; }
.e365 .e365-back { background: none; border: none; padding: 0; margin: 0 0 10px; font: inherit; font-size: .85rem; color: var(--e365-primary); cursor: pointer; text-decoration: underline; }
.e365 .e365-req { color: #dc2626; margin-left: 2px; }
.e365 .e365-hint { color: var(--e365-muted); font-size: .75rem; margin-top: 3px; }
.e365 .e365-hp { position: absolute !important; left: -10000px !important; width: 1px; height: 1px; overflow: hidden; }
`;

function dateLabel(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.slice(0, 10) + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function metaLine(p: JobPosting): string {
  const bits = [p.employmentTypeLabel];
  if (p.payText) bits.push(p.payText);
  if (p.startDate) bits.push(`Starts ${dateLabel(p.startDate)}`);
  if (p.closeDate) bits.push(`Apply by ${dateLabel(p.closeDate)}`);
  return bits.filter(Boolean).join(' · ');
}

function ageOn(dobIso: string, today: Date): number {
  const [y, m, d] = dobIso.split('-').map(Number);
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
  return age;
}

function setJobParam(id: string | null): void {
  try {
    const u = new URL(location.href);
    if (id) u.searchParams.set('job', id); else u.searchParams.delete('job');
    history.replaceState(null, '', u.toString());
  } catch { /* ignore */ }
}

export function mountJobs(mount: HTMLElement, cfg: EmbedConfig, theme: ThemeOptions, venueKey: string): void {
  const { root, shadow } = createRoot(mount, theme);
  const style = document.createElement('style');
  style.textContent = JOBS_CSS;
  shadow.insertBefore(style, root);

  const base = `${cfg.baseApiUrl}/api/embed/${encodeURIComponent(venueKey)}/jobs`;
  let postings: JobPosting[] = [];

  async function load(): Promise<void> {
    renderLoading(root, TITLE);
    try {
      let res: Response;
      try { res = await fetch(base); }
      catch (e) { const down = await handleFetchFailure(null); if (down) return; throw e; }
      if (res.status === 503 && await handleFetchFailure(res)) return; // maintenance card took over
      if (!res.ok) throw new Error(String(res.status));
      postings = (await res.json()) as JobPosting[];
    } catch {
      clear(root);
      root.append(
        h('h3', undefined, TITLE),
        h('div', { class: 'e365-alert error' }, 'We couldn’t load our job openings just now.'),
      );
      const retry = h('button', { class: 'e365-btn', type: 'button' }, 'Try again');
      retry.addEventListener('click', () => void load());
      root.append(retry, footer());
      return;
    }
    const wanted = new URLSearchParams(location.search).get('job');
    const match = wanted ? postings.find((p) => p.id === wanted) : undefined;
    if (match) renderDetail(match); else { if (wanted) setJobParam(null); renderList(); }
  }

  function renderList(): void {
    clear(root);
    root.append(h('h3', undefined, TITLE));
    if (postings.length === 0) {
      root.append(h('div', { class: 'e365-alert info' }, 'No openings right now — check back soon.'), footer());
      return;
    }
    root.append(h('div', { class: 'e365-muted', style: 'margin:-6px 0 12px' },
      `${postings.length} open position${postings.length === 1 ? '' : 's'} at ${cfg.venueName}.`));
    for (const p of postings) {
      const excerpt = p.description.length > 220 ? p.description.slice(0, 220).trimEnd() + '…' : p.description;
      const btn = h('button', { class: 'e365-btn', type: 'button' }, 'View & apply');
      btn.addEventListener('click', () => { setJobParam(p.id); renderDetail(p); });
      root.append(h('div', { class: 'e365-job' },
        h('h4', undefined, p.title),
        h('div', { class: 'e365-job-meta' }, metaLine(p)),
        h('div', { class: 'e365-job-desc' }, excerpt),
        btn,
      ));
    }
    root.append(footer());
  }

  function backButton(): HTMLElement {
    const b = h('button', { class: 'e365-back', type: 'button' }, '← All openings');
    b.addEventListener('click', () => { setJobParam(null); renderList(); });
    return b;
  }

  function renderDetail(p: JobPosting): void {
    clear(root);
    const apply = h('button', { class: 'e365-btn', type: 'button' }, 'Apply for this job');
    apply.addEventListener('click', () => renderForm(p));
    root.append(
      backButton(),
      h('h3', undefined, p.title),
      h('div', { class: 'e365-job-meta' }, metaLine(p)),
      h('div', { class: 'e365-job-desc' }, p.description),
      h('div', { class: 'e365-muted', style: 'margin-top:10px' }, `Applicants must be at least ${p.minimumAge} years old.`),
      apply,
      footer(),
    );
  }

  function field(label: string, control: HTMLElement, required = false, hint?: string): HTMLElement {
    const id = control.getAttribute('id') || '';
    const wrap = h('div');
    wrap.append(h('label', { for: id }, label, required ? h('span', { class: 'e365-req', 'aria-hidden': 'true' }, '*') : null), control);
    if (hint) wrap.append(h('div', { class: 'e365-hint' }, hint));
    return wrap;
  }

  function renderForm(p: JobPosting): void {
    clear(root);
    const renderedAt = Date.now();
    const started = performance.now();
    const uid = Math.random().toString(36).slice(2, 8);

    const input = (name: string, type: string, attrs: Record<string, string | boolean> = {}): HTMLInputElement =>
      h('input', { id: `e365-${name}-${uid}`, name, type, ...attrs }) as HTMLInputElement;

    const first = input('firstName', 'text', { autocomplete: 'given-name', maxlength: '100', required: true });
    const last = input('lastName', 'text', { autocomplete: 'family-name', maxlength: '100', required: true });
    const email = input('email', 'email', { autocomplete: 'email', maxlength: '254', required: true });
    const phone = input('phone', 'tel', { autocomplete: 'tel', maxlength: '40' });
    const today = new Date();
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const dob = input('dateOfBirth', 'date', { max: todayIso, min: '1900-01-01', autocomplete: 'bday', required: true });
    const resume = input('resume', 'file', { accept: ALLOWED_EXT.join(',') });
    // Honeypot: off-screen, not focusable, ignored by autofill — only a bot fills it in.
    const hp = h('input', { type: 'text', name: 'website', tabindex: '-1', autocomplete: 'off', 'aria-hidden': 'true' }) as HTMLInputElement;

    const answers = p.questions.map((q, i) => {
      const ta = h('textarea', { id: `e365-answer${i}-${uid}`, name: `answer${i}`, maxlength: '4000' }) as HTMLTextAreaElement;
      if (q.required) ta.required = true;
      return { q, ta };
    });

    const msg = h('div', { role: 'alert', 'aria-live': 'polite' });
    const submit = h('button', { class: 'e365-btn', type: 'submit' }, 'Submit application') as HTMLButtonElement;

    const form = h('form', { novalidate: true }) as HTMLFormElement;
    form.append(
      backButton(),
      h('h3', undefined, `Apply: ${p.title}`),
      h('div', { class: 'e365-row' }, field('First name', first, true), field('Last name', last, true)),
      h('div', { class: 'e365-row' }, field('Email', email, true), field('Phone', phone)),
      field('Date of birth', dob, true, `You must be at least ${p.minimumAge} to apply for this position.`),
      ...answers.map(({ q, ta }) => field(q.prompt, ta, q.required)),
      field('Résumé (optional)', resume, false, 'PDF, Word (.docx/.doc), RTF or TXT · up to 5 MB'),
      h('div', { class: 'e365-hp' }, h('label', undefined, 'Website', hp)),
      msg,
      submit,
      h('div', { class: 'e365-hint', style: 'margin-top:8px' },
        `Your application goes to ${cfg.venueName}. It’s kept only as long as the venue needs it for hiring.`),
      footer(),
    );
    root.append(form);

    const showError = (text: string): void => {
      clear(msg);
      msg.append(h('div', { class: 'e365-alert error' }, text));
    };

    form.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      if (submit.disabled) return; // double-submit guard
      clear(msg);

      // ---- client-side checks (the server re-checks all of these)
      if (!first.value.trim() || !last.value.trim()) return showError('Please enter your first and last name.');
      if (!email.value.trim() || !email.checkValidity()) return showError('Please enter a valid email address.');
      if (!dob.value) return showError('Please enter your date of birth.');
      if (dob.value > todayIso) return showError('Please enter a valid date of birth.');
      if (ageOn(dob.value, today) < p.minimumAge)
        return showError(`Sorry — applicants for this position must be at least ${p.minimumAge} years old.`);
      for (const { q, ta } of answers)
        if (q.required && !ta.value.trim()) return showError(`Please answer: ${q.prompt}`);
      const file = resume.files && resume.files[0];
      if (file) {
        const name = file.name.toLowerCase();
        if (!ALLOWED_EXT.some((e) => name.endsWith(e)))
          return showError('Please upload your résumé as a PDF, Word (.docx or .doc), RTF or plain-text file.');
        if (file.size > MAX_BYTES) return showError('The résumé must be 5 MB or smaller.');
        if (file.size === 0) return showError('That résumé file is empty.');
      }

      const fd = new FormData();
      fd.append('firstName', first.value.trim());
      fd.append('lastName', last.value.trim());
      fd.append('email', email.value.trim());
      fd.append('phone', phone.value.trim());
      fd.append('dateOfBirth', dob.value);
      answers.forEach(({ ta }, i) => fd.append(`answer${i}`, ta.value.trim()));
      if (file) fd.append('resume', file, file.name);
      fd.append('website', hp.value);
      fd.append('renderedAt', String(renderedAt));
      fd.append('elapsedMs', String(Math.round(performance.now() - started)));
      // Website analytics attribution: optional form field; the API ignores it when absent.
      const wa = trackIds();
      if (wa) fd.append('wa', wa);

      submit.disabled = true;
      submit.textContent = 'Submitting…';
      track('job_apply_start', 'jobs');
      try {
        const res = await fetch(`${base}/${encodeURIComponent(p.id)}/apply`, { method: 'POST', body: fd });
        if (res.ok) { renderSubmitted(p); return; }
        let serverMsg = '';
        try { serverMsg = ((await res.json()) as { message?: string }).message || ''; } catch { /* not json */ }
        if (res.status === 429) showError('You’ve sent several applications from this network in the last hour. Please try again later.');
        else if (res.status === 404) showError(serverMsg || 'Sorry — this position is no longer accepting applications.');
        else if (res.status === 413) showError('Your application was too large. The résumé must be 5 MB or smaller.');
        else showError(serverMsg || 'Something went wrong sending your application. Please try again.');
      } catch {
        showError('We couldn’t reach the server. Check your connection and try again — your answers are still here.');
      } finally {
        if (submit.isConnected) { submit.disabled = false; submit.textContent = 'Submit application'; }
      }
    });
  }

  function renderSubmitted(p: JobPosting): void {
    clear(root);
    setJobParam(null);
    const more = h('button', { class: 'e365-btn', type: 'button' }, 'See other openings');
    more.addEventListener('click', () => renderList());
    root.append(
      h('h3', undefined, 'Application sent'),
      h('div', { class: 'e365-alert info' }, `✓ Thanks — ${cfg.venueName} will be in touch about the ${p.title} position.`),
      h('div', { class: 'e365-muted' }, 'We’ve emailed you a confirmation.'),
      more,
      footer(),
    );
  }

  void load();
}
